<template>
  <ele-card v-bind="attrs">
    <el-form
      class="table-search-form"
      @keyup.enter.prevent="search"
      @submit.prevent=""
    >
      <el-row :gutter="12">
        <el-col
          :lg="3"
          :md="8"
          :sm="12"
          :xs="24"
          v-for="(column, index) in searchColumns"
          :key="index"
        >
          <template v-if="column.isDate">
            <div class="el-input el-input--suffix">
              <div class="el-input__wrapper">
                <lzd-date-picker
                  v-model="ufd.form[column.prop!]"
                  clearable
                  :type="column.dateType ?? 'datetime'"
                  :format="column.dateFormat ?? 'YYYY-MM-DD HH:mm:ss'"
                  :placeholder="
                    LzdUtil.i18nFormItemLabel(searchColumns, column.prop!)
                  "
                >
                  <template #footer>66</template>
                </lzd-date-picker>
              </div>
            </div>
          </template>
          <template v-else-if="column.isAutocomplete">
            <el-autocomplete
              v-model="ufd.form[column.prop!]"
              :fetch-suggestions="
                (queryString, cb) =>
                  querySearch(
                    queryString,
                    cb,
                    isRef(column.items)
                      ? column.items?.value
                      : Array.isArray(column.items)
                        ? column.items
                        : []
                  )
              "
              :placeholder="
                LzdUtil.i18nFormItemLabel(searchColumns, column.prop!)
              "
              value-key="k"
              clearable
            />
          </template>
          <template v-else-if="!column.isSelect">
            <el-input
              clearable
              v-model.trim="ufd.form[column.prop!]"
              :placeholder="
                LzdUtil.i18nFormItemLabel(searchColumns, column.prop!)
              "
            />
          </template>
          <template v-else-if="column.items">
            <el-select
              v-model="ufd.form[column.prop!]"
              filterable
              clearable
              :placeholder="
                LzdUtil.i18nFormItemLabel(searchColumns, column.prop!)
              "
              @change="collegeChange(column.prop!)"
            >
              <el-option
                v-for="(item, itemIndex) in isRef(column.items)
                  ? column.items.value
                  : Array.isArray(column.items)
                    ? column.items
                    : column.items()"
                :label="item.l ?? item.v"
                :value="item.v"
                :key="itemIndex"
                v-show="
                  column.prop !== 'major' ||
                  !collegeVal ||
                  collegeVal == item.item
                "
              />
            </el-select>
          </template>
        </el-col>
        <el-col :lg="3" :md="8" :sm="12" :xs="24" style="display: flex">
          <el-button type="primary" @click="search">{{
            t('global.btns.search')
          }}</el-button>
          <el-button v-if="showReset" @click="reset">{{
            t('global.btns.reset')
          }}</el-button>
          <slot name="actions"></slot>
        </el-col>
      </el-row>
    </el-form>
  </ele-card>
</template>

<script lang="ts" setup>
  import { computed, useAttrs, isRef, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useFormData } from '@/utils/use-form-data';
  import type { KV, LzdColumns } from '@/api';
  import LzdUtil from '@/utils/lzd-util';

  const attrs = useAttrs();
  const { t } = useI18n();

  const props = withDefaults(
    defineProps<{
      /** 数据表格字段 */
      columns: LzdColumns;
      /** 是否显示重置按钮，默认显示 */
      showReset?: boolean;
    }>(),
    { showReset: true }
  );

  const emit = defineEmits<{
    (e: 'search', where?: Record<string, any>): void;
  }>();

  /** 表单数据 */
  const ufd = computed(() => {
    const data: Record<string, any> = {};
    props.columns
      .filter((v) => v.isSearch && v.prop)
      .forEach((v) => (data[v.prop!] = v.value));
    return useFormData(data);
  });

  const searchColumns = computed(() => {
    return props.columns.filter((v) => v.isSearch && v.prop);
  });

  /** 搜索 */
  const search = () => {
    emit('search', { ...(ufd.value.form ?? {}) });
  };

  /**  联动学院与专业 */
  const collegeVal = ref<string>();
  const collegeChange = (prop: string) => {
    if (prop == 'college') {
      collegeVal.value = ufd.value.form.college;
    }
  };

  const querySearch = (queryString: string, cb: any, items: KV[]) => {
    const results = queryString
      ? items.filter((item) =>
          item.k?.toLowerCase().includes(queryString.toLowerCase())
        )
      : items;
    cb(results);
  };

  /**  重置 */
  const reset = () => {
    ufd.value.resetFields();
    search();
  };
  defineExpose({
    reset,
    form: ufd.value.form
  });
</script>

<script lang="ts">
  export default {
    name: 'LzdDataTableSearch'
  };
</script>

<style lang="scss" scoped></style>
